import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './d-edit-slide';
import type { CustomSlide, RssNewsItem, Slide, SlideSuggestionCategory } from 'src/content/screens/slide-element';
import { uuid } from 'src/utilities/text';
import { BaseDialog } from 'src/library/components/BaseDialog';
import type { EditSlideDialogInput } from 'src/content/screens/edit-slide-dialog-input';
import type { EditSlideDialogResult } from 'src/content/screens/edit-slide-dialog-result';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import type { EmployeePresenceStatus } from 'src/store/selectors/staffing/employee-presence-status';
import type { LibraryItem } from 'src/library/screen/library-item';
import { assetAvailability } from 'src/services/asset-availability';
import { removeLibraryItem } from 'src/store/signage';
import type { ScreenMediaUploadPayload } from './screen-media-upload-payload';
import { invalidateAssetUsage, getSavedSlideUsage, invalidateSavedSlideUsage } from './asset-usage';
import { AlertDialog } from 'src/library/components/alert-dialog';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-edit-slide-dialog')
export class DEditSlideDialog extends BaseDialog<EditSlideDialogInput, EditSlideDialogResult> {
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      dialog[open] {
        width: 100%;
        height: 100vh;
        max-width: none;
        max-height: none;
        background: none;
        box-shadow: none;
        filter: drop-shadow(0 16px 24px hsla(0, 0%, 0%, 0.5));
      }
      ::backdrop {
        background-color: hsla(197, 82%, 15%, 0.7);
        backdrop-filter: blur(3px);
      }
      @media (min-width: 1200px) {
        div[slot='actions'] {
          transform: scale(1.3);
          transform-origin: right;
        }
      }
    `,
  ];
  @property({ type: Boolean })
  dark = true;
  @property({ type: Object })
  slide!: Slide;
  @property({ type: Array })
  templates: CustomSlide[] = [];
  @property({ type: Array })
  newsFeed: RssNewsItem[] = [];
  @property({ type: Array })
  newsFeedFhi: RssNewsItem[] = [];
  @property({ type: Array })
  suggestionsSelected: Slide[] = [];
  @property({ type: Array })
  suggestionsCategorized: SlideSuggestionCategory[] = [];
  @property({ type: Array })
  media: string[] = [];
  @property({ type: Array })
  library: LibraryItem[] = [];
  @property({ type: Array })
  employeesPresenceStatus: EmployeePresenceStatus[] = [];
  @property({ type: Number })
  screenId = 0;
  @property({ type: Number })
  organizationId = 0;
  @property({ type: String })
  selectedMedia = '';
  @property({ type: Boolean })
  newSlide = false;
  @property({ type: String })
  previewUrl = '';
  @property({ type: String })
  assetBase = '';
  @state()
  private usedSavedSlideIds: Set<string> = new Set();

  protected get calculatedFooterActions(): DialogActionInput[] {
    return [
      { name: t('screens.cancel'), action: 'cancel' },
      {
        name: t('screens.done'),
        action: 'done',
        main: true,
      },
    ];
  }

  private async fetchSavedSlideUsage() {
    if (!this.organizationId) return;
    this.usedSavedSlideIds = await getSavedSlideUsage(this.organizationId);
  }

  private onSlideChanged(e: CustomEvent<{ value: Slide }>) {
    e.stopPropagation();
    this.slide = e.detail.value;
  }

  private onSelectMedia(e: CustomEvent<{ src: string }>) {
    e.stopPropagation();
    this.selectedMedia = e.detail.src;
  }

  private onAssetDeleted(e: CustomEvent<{ id: string; path: string }>) {
    e.stopPropagation();
    // d-media-gallery oppdaterer sin egen library-prop lokalt etter sletting,
    // men dialogens egen library-state må også reflektere endringen så andre
    // komponenter (gallery-popovere på andre slides) ser ferskt sett.
    this.library = this.library.filter((i) => i.id !== e.detail.id);
  }

  private async onSavedSlideDelete(e: CustomEvent<{ id: string }>) {
    e.stopPropagation();
    if (!this.organizationId) return;
    try {
      await removeLibraryItem(this.organizationId, e.detail.id);
      invalidateAssetUsage(this.organizationId);
      invalidateSavedSlideUsage(this.organizationId);
      this.library = this.library.filter((i) => i.id !== e.detail.id);
      this.fetchSavedSlideUsage();
    } catch (err) {
      console.error('[d-edit-slide-dialog] saved-slide delete failed:', err);
      const is409 = err instanceof Error && err.message.includes('409');
      await AlertDialog.open({
        title: t('screens.deleteFailedTitle'),
        content: is409 ? t('screens.editSlideDialog.slideInUse') : t('screens.deleteFailedGeneric'),
      });
    }
  }

  private onUploadMedia(
    e: CustomEvent<{ file: File; name: string; path: string; mediaType: 'image' | 'video'; mimeType: string }>,
  ) {
    e.stopPropagation();
    // Optimistisk: legg det opplastede til library lokalt så galleri-popoveren
    // viser nye items umiddelbart. Dialogen er en detached node (åpnet via
    // BaseDialog.open) og mottar ikke library-oppdateringer fra app-state mens
    // den er åpen, så uten dette ser brukeren ingenting før dialogen lukkes
    // og åpnes på nytt. `path` kommer fra d-media-gallery som vet at video
    // konverteres til mp4 (slug-based asset URL), så vi gjetter ikke selv.
    const optimistic: LibraryItem = {
      id: `pending-${e.detail.name}`,
      name: e.detail.name,
      imageSrc: '',
      defaultDuration: 0,
      standardItem: false,
      assets: [e.detail.path],
      signageMediaType: e.detail.mediaType,
    };
    this.library = [...this.library, optimistic];

    // Registrér forventet asset-path så d-loading-video/d-loading-image
    // venter på library-bekreftelse i stedet for å spamme 404 på asset-URL-en.
    // Dialogen lever ikke nødvendigvis like lenge som konverteringen, men
    // service-en er en singleton som overlever dialog-close → polling
    // fortsetter til pathen dukker opp i library.
    if (this.organizationId) {
      assetAvailability.awaitPath(this.organizationId, e.detail.path);
    }

    this.dispatchEvent(
      new CustomEvent<ScreenMediaUploadPayload>('add-screen-media', {
        bubbles: true,
        composed: true,
        detail: {
          screenId: this.screenId,
          organizationId: this.organizationId,
          name: e.detail.name,
          mimeType: e.detail.mimeType,
          file: e.detail.file,
        },
      }),
    );
  }

  protected initializeDialog(input: EditSlideDialogInput) {
    this.templates = input.templates;
    this.media = input.media;
    this.library = input.library;
    this.suggestionsSelected = input.suggestionsSelected;
    this.suggestionsCategorized = input.suggestionsCategorized;
    this.employeesPresenceStatus = input.employeesPresenceStatus;
    this.newsFeed = input.newsFeed;
    this.newsFeedFhi = input.newsFeedFhi;
    this.screenId = input.screenId;
    this.organizationId = input.organizationId;
    this.fetchSavedSlideUsage();
    this.previewUrl = input.previewUrl ?? '';
    this.assetBase = input.assetBase ?? '';
    if (input.slide) {
      this.slide = input.slide;
      if (input.slide.type === 'custom') {
        this.selectedMedia = input.slide.media;
      }
    } else {
      this.newSlide = true;
      const firstTemplate = this.templates[0];
      if (!firstTemplate) {
        // Konfigurasjonsfeil — hvis vi åpner "ny slide"-dialog uten maler
        // er det ingenting å initialisere med. Kaster så kaller kan reagere.
        throw new Error('Cannot create new slide: templates array is empty');
      }
      // Tom navn så header-input fyller den i auto (se d-custom-slide.updateSlide).
      this.slide = { ...firstTemplate, name: '', id: uuid() };
    }
  }

  protected fetchResult(detail: string | undefined): EditSlideDialogResult {
    if (detail === 'done') {
      return {
        action: 'done',
        newSlide: this.newSlide,
        slide: this.slide,
      };
    } else {
      return {
        action: 'cancel',
      };
    }
  }

  render() {
    return html`
      <dialog id="popup" @close=${this.onClose}>
        <d-edit-slide
          .slide=${this.slide}
          .selectedMedia=${this.selectedMedia}
          .templates=${this.templates}
          .suggestionsSelected=${this.suggestionsSelected}
          .suggestionsCategorized=${this.suggestionsCategorized}
          .newsFeed=${this.newsFeed}
          .newsFeedFhi=${this.newsFeedFhi}
          .employeesPresenceStatus=${this.employeesPresenceStatus}
          .media=${this.media}
          .library=${this.library}
          .assetBase=${this.assetBase}
          .usedSavedSlideIds=${this.usedSavedSlideIds}
          @value-changed=${(e: CustomEvent<{ value: Slide }>) => this.onSlideChanged(e)}
          @select-media=${(e: CustomEvent<{ src: string }>) => this.onSelectMedia(e)}
          @upload-media=${(
            e: CustomEvent<{ file: File; name: string; path: string; mediaType: 'image' | 'video'; mimeType: string }>,
          ) => this.onUploadMedia(e)}
          @asset-deleted=${(e: CustomEvent<{ id: string; path: string }>) => this.onAssetDeleted(e)}
          @saved-slide-delete=${(e: CustomEvent<{ id: string }>) => this.onSavedSlideDelete(e)}
        >
          <div slot="actions">${this.renderFooterActions()}</div>
        </d-edit-slide>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-edit-slide-dialog': DEditSlideDialog;
  }
}
